import React, { useEffect, useState } from 'react';
import { adminApi } from '../api';
import { PAGE_DEFAULTS, mergePageContent } from '../content/pageDefaults';

const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white';

const HOME_SECTIONS = [
  {
    title: 'Hero banner',
    fields: [
      { path: 'hero.eyebrow', label: 'Small label' },
      { path: 'hero.title', label: 'Heading' },
      { path: 'hero.titleAccent', label: 'Accent line' },
      { path: 'hero.subtitle', label: 'Description', type: 'textarea' },
      { path: 'hero.primaryButton', label: 'Primary button' },
      { path: 'hero.secondaryButton', label: 'Secondary button' },
      { path: 'hero.image', label: 'Background image', type: 'image' },
    ],
  },
  {
    title: 'Hero numbers',
    list: { path: 'stats', empty: { value: '', label: '' }, columns: [{ key: 'value', label: 'Number' }, { key: 'label', label: 'Label' }] },
  },
  { title: 'Scrolling ticker', list: { path: 'marquee', primitive: 'text', empty: '' } },
  {
    title: 'About block',
    fields: [
      { path: 'about.eyebrow', label: 'Small label' },
      { path: 'about.title', label: 'Heading' },
      { path: 'about.cta', label: 'Button text' },
      { path: 'about.badgeValue', label: 'Badge number' },
      { path: 'about.badgeLabel', label: 'Badge label' },
      { path: 'about.image', label: 'Image', type: 'image' },
    ],
    lists: [
      { title: 'Paragraphs', list: { path: 'about.paragraphs', primitive: 'textarea', empty: '' } },
      { title: 'Highlights', list: { path: 'about.highlights', primitive: 'text', empty: '' } },
    ],
  },
  {
    title: 'Category heading',
    fields: [
      { path: 'journeys.eyebrow', label: 'Small label' },
      { path: 'journeys.title', label: 'Heading' },
    ],
  },
  {
    title: 'Featured packages heading',
    fields: [
      { path: 'featured.eyebrow', label: 'Small label' },
      { path: 'featured.title', label: 'Heading' },
      { path: 'featured.cta', label: 'Link text' },
    ],
  },
  {
    title: 'Shop heading',
    fields: [
      { path: 'shop.eyebrow', label: 'Small label' },
      { path: 'shop.title', label: 'Heading' },
    ],
  },
  {
    title: 'Why choose us',
    fields: [
      { path: 'why.eyebrow', label: 'Small label' },
      { path: 'why.title', label: 'Heading' },
    ],
    list: { path: 'why.items', empty: { title: '', desc: '' }, columns: [{ key: 'title', label: 'Title' }, { key: 'desc', label: 'Description', type: 'textarea' }] },
  },
  {
    title: 'Testimonials',
    fields: [
      { path: 'testimonials.eyebrow', label: 'Small label' },
      { path: 'testimonials.title', label: 'Heading' },
    ],
    list: {
      path: 'testimonials.items',
      empty: { name: '', review: '', pkg: '' },
      columns: [
        { key: 'name', label: 'Name' },
        { key: 'pkg', label: 'Package' },
        { key: 'review', label: 'Review', type: 'textarea' },
      ],
    },
  },
  {
    title: 'Booking steps',
    fields: [
      { path: 'process.eyebrow', label: 'Small label' },
      { path: 'process.title', label: 'Heading' },
    ],
    list: {
      path: 'process.steps',
      empty: { step: '', title: '', desc: '' },
      columns: [
        { key: 'step', label: 'Step' },
        { key: 'title', label: 'Title' },
        { key: 'desc', label: 'Description', type: 'textarea' },
      ],
    },
  },
  {
    title: 'Questions',
    fields: [
      { path: 'faq.eyebrow', label: 'Small label' },
      { path: 'faq.title', label: 'Heading' },
    ],
    list: {
      path: 'faq.items',
      empty: { q: '', a: '' },
      columns: [
        { key: 'q', label: 'Question' },
        { key: 'a', label: 'Answer', type: 'textarea' },
      ],
    },
  },
  {
    title: 'Contact heading',
    fields: [
      { path: 'contact.eyebrow', label: 'Small label' },
      { path: 'contact.title', label: 'Heading' },
      { path: 'contact.subtitle', label: 'Description', type: 'textarea' },
    ],
  },
  {
    title: 'Blog heading',
    fields: [
      { path: 'blog.eyebrow', label: 'Small label' },
      { path: 'blog.title', label: 'Heading' },
      { path: 'blog.cta', label: 'Link text' },
    ],
  },
  {
    title: 'Bottom banner',
    fields: [
      { path: 'cta.eyebrow', label: 'Small label' },
      { path: 'cta.title', label: 'Heading' },
      { path: 'cta.subtitle', label: 'Description', type: 'textarea' },
      { path: 'cta.primaryButton', label: 'Primary button' },
      { path: 'cta.secondaryButton', label: 'Secondary button' },
      { path: 'cta.image', label: 'Background image', type: 'image' },
    ],
  },
];

const ABOUT_SECTIONS = [
  {
    title: 'Hero',
    fields: [
      { path: 'hero.title', label: 'Heading' },
      { path: 'hero.subtitle', label: 'Subtitle', type: 'textarea' },
    ],
  },
  {
    title: 'Our story',
    fields: [
      { path: 'story.title', label: 'Heading' },
      { path: 'story.image', label: 'Image', type: 'image' },
    ],
    list: { path: 'story.paragraphs', primitive: 'textarea', empty: '' },
  },
  {
    title: 'Mission',
    fields: [
      { path: 'mission.icon', label: 'Icon' },
      { path: 'mission.title', label: 'Heading' },
      { path: 'mission.text', label: 'Text', type: 'textarea' },
    ],
  },
  {
    title: 'Vision',
    fields: [
      { path: 'vision.icon', label: 'Icon' },
      { path: 'vision.title', label: 'Heading' },
      { path: 'vision.text', label: 'Text', type: 'textarea' },
    ],
  },
  {
    title: 'Core values',
    fields: [
      { path: 'values.title', label: 'Heading' },
      { path: 'values.subtitle', label: 'Subtitle' },
    ],
    list: {
      path: 'values.items',
      empty: { icon: '', title: '', text: '' },
      columns: [
        { key: 'icon', label: 'Icon' },
        { key: 'title', label: 'Title' },
        { key: 'text', label: 'Text', type: 'textarea' },
      ],
    },
  },
  {
    title: 'Statistics',
    list: { path: 'stats', empty: { value: '', label: '' }, columns: [{ key: 'value', label: 'Number' }, { key: 'label', label: 'Label' }] },
  },
  {
    title: 'Team',
    fields: [
      { path: 'team.title', label: 'Heading' },
      { path: 'team.subtitle', label: 'Subtitle' },
    ],
    list: {
      path: 'team.members',
      empty: { name: '', role: '', image: '' },
      columns: [
        { key: 'name', label: 'Name' },
        { key: 'role', label: 'Role' },
        { key: 'image', label: 'Photo', type: 'image' },
      ],
    },
  },
  {
    title: 'Licences',
    fields: [
      { path: 'certifications.title', label: 'Heading' },
      { path: 'certifications.subtitle', label: 'Subtitle' },
    ],
    list: {
      path: 'certifications.items',
      empty: { icon: '', title: '', text: '' },
      columns: [
        { key: 'icon', label: 'Icon' },
        { key: 'title', label: 'Title' },
        { key: 'text', label: 'Text' },
      ],
    },
  },
  {
    title: 'Closing banner',
    fields: [
      { path: 'cta.title', label: 'Heading' },
      { path: 'cta.subtitle', label: 'Description', type: 'textarea' },
      { path: 'cta.primaryButton', label: 'Primary button' },
      { path: 'cta.secondaryButton', label: 'Secondary button' },
    ],
  },
];

const SCHEMAS = { home: HOME_SECTIONS, about: ABOUT_SECTIONS };

function getPath(obj, path) {
  return path.split('.').reduce((current, key) => (current == null ? undefined : current[key]), obj);
}

function setPath(obj, path, value) {
  const keys = path.split('.');
  const clone = (node) => (Array.isArray(node) ? [...node] : { ...(node || {}) });
  const root = clone(obj);
  let cursor = root;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = clone(cursor[key]);
    cursor = cursor[key];
  });
  return root;
}

function TextControl({ label, value, onChange, type = 'text' }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      {type === 'textarea' ? (
        <textarea rows={3} className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function ImageControl({ label, value, onChange, onError }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminApi.uploadPageImage(file);
      onChange(data.url);
    } catch (error) {
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      <div className="flex flex-col sm:flex-row gap-3">
        {value ? <img src={value} alt="" className="w-full sm:w-28 h-20 object-cover rounded-lg border border-gray-200" /> : null}
        <div className="flex-1 space-y-2">
          <input className={inputClass} value={value || ''} placeholder="Image URL" onChange={(e) => onChange(e.target.value)} />
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            {uploading ? 'Uploading...' : 'Upload image'}
          </label>
        </div>
      </div>
    </div>
  );
}

function ListEditor({ spec, content, onChange, onError }) {
  const items = getPath(content, spec.path) || [];
  const updateItem = (index, next) => {
    const copy = [...items];
    copy[index] = next;
    onChange(setPath(content, spec.path, copy));
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="border border-gray-100 rounded-xl p-3 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500">Item {index + 1}</p>
            <button
              type="button"
              onClick={() => onChange(setPath(content, spec.path, items.filter((_, i) => i !== index)))}
              className="text-xs font-semibold text-red-600">
              Remove
            </button>
          </div>
          {spec.primitive ? (
            <TextControl label="Text" type={spec.primitive} value={item} onChange={(value) => updateItem(index, value)} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {spec.columns.map((column) => (
                column.type === 'image' ? (
                  <ImageControl
                    key={column.key}
                    label={column.label}
                    value={item?.[column.key]}
                    onError={onError}
                    onChange={(value) => updateItem(index, { ...item, [column.key]: value })}
                  />
                ) : (
                  <TextControl
                    key={column.key}
                    label={column.label}
                    type={column.type}
                    value={item?.[column.key]}
                    onChange={(value) => updateItem(index, { ...item, [column.key]: value })}
                  />
                )
              ))}
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange(setPath(content, spec.path, [...items, spec.primitive ? spec.empty : { ...spec.empty }]))}
        className="text-sm font-semibold text-purple-700">
        + Add item
      </button>
    </div>
  );
}

function SectionCard({ section, content, onChange, onError }) {
  return (
    <div className="bg-white rounded-2xl shadow p-5 space-y-4">
      <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
      {section.fields?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {section.fields.map((field) => (
            field.type === 'image' ? (
              <ImageControl
                key={field.path}
                label={field.label}
                value={getPath(content, field.path)}
                onError={onError}
                onChange={(value) => onChange(setPath(content, field.path, value))}
              />
            ) : (
              <TextControl
                key={field.path}
                label={field.label}
                type={field.type}
                value={getPath(content, field.path)}
                onChange={(value) => onChange(setPath(content, field.path, value))}
              />
            )
          ))}
        </div>
      )}
      {section.list && <ListEditor spec={section.list} content={content} onChange={onChange} onError={onError} />}
      {section.lists?.map((nested) => (
        <div key={nested.title}>
          <p className="text-sm font-semibold text-gray-700 mb-2">{nested.title}</p>
          <ListEditor spec={nested.list} content={content} onChange={onChange} onError={onError} />
        </div>
      ))}
    </div>
  );
}

export default function PageContentEditor({ showError, showSuccess }) {
  const [tab, setTab] = useState('home');
  const [pages, setPages] = useState({ home: HOME_FALLBACK('home'), about: HOME_FALLBACK('about') });
  const [updatedAt, setUpdatedAt] = useState({ home: null, about: null });
  const [dirty, setDirty] = useState({ home: false, about: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [home, about] = await Promise.all([
          adminApi.getPageContent('home'),
          adminApi.getPageContent('about'),
        ]);
        if (!active) return;
        setPages({
          home: mergePageContent(PAGE_DEFAULTS.home, home.content),
          about: mergePageContent(PAGE_DEFAULTS.about, about.content),
        });
        setUpdatedAt({ home: home.updated_at, about: about.updated_at });
      } catch (error) {
        showError(error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // Load once when the editor opens. showError is recreated by the dashboard on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePage = (next) => {
    setPages((current) => ({ ...current, [tab]: next }));
    setDirty((current) => ({ ...current, [tab]: true }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await adminApi.savePageContent(tab, pages[tab]);
      setPages((current) => ({ ...current, [tab]: mergePageContent(PAGE_DEFAULTS[tab], saved.content) }));
      setUpdatedAt((current) => ({ ...current, [tab]: saved.updated_at }));
      setDirty((current) => ({ ...current, [tab]: false }));
      showSuccess(tab === 'home' ? 'Home page updated' : 'About page updated');
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setPages((current) => ({ ...current, [tab]: structuredClone(PAGE_DEFAULTS[tab]) }));
    setDirty((current) => ({ ...current, [tab]: true }));
  };

  return (
    <div>
      <div className="bg-white rounded-2xl shadow p-4 mb-6 flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex gap-2">
          {['home', 'about'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
              {key === 'home' ? 'Home page' : 'About us'}
              {dirty[key] ? ' •' : ''}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 lg:ml-auto">
          {updatedAt[tab] ? `Last saved ${new Date(updatedAt[tab]).toLocaleString()}` : 'Not saved yet'}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={reset} className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700">
            Reset text
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white disabled:opacity-60">
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Edit the words and pictures visitors see. Packages, categories, and blog posts stay in their own sections.
      </p>

      {loading ? (
        <div className="bg-white rounded-2xl shadow p-8 text-gray-500">Loading page content...</div>
      ) : (
        <div className="space-y-4">
          {SCHEMAS[tab].map((section) => (
            <SectionCard
              key={section.title}
              section={section}
              content={pages[tab]}
              onChange={updatePage}
              onError={showError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HOME_FALLBACK(slug) {
  return structuredClone(PAGE_DEFAULTS[slug]);
}
